import React, { useState } from 'react';
import { apiFetch } from 'api/api';
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { getQueryParams } from 'utils/Utils'
import CustomTab from 'components/tabs/CustomTab';
import Loader from 'components/loader/Loader';
import SectionError from 'components/error/SectionError';

const T = {
  bg:   '#F6F5F0',
  card: '#FFFFFF',
  line: '#E5E2D8',
  ink:  '#232821',
  mut:  '#6E766C',
  acc:  '#2E9E7A',
};

const GraphContainer = (props) => {
    const { tabs, xkey, setUrl } = props

    const history = useHistory()
    const querryParams = getQueryParams(history.location.search)

    const [activeTab, setActiveTab] = useState(0)
    const [selected, setSelected] = React.useState([]);
    const [seriesData, setSeriesData] = React.useState([])
    const [fetchError, setFetchError] = React.useState(null);
    // Tracks loading during tab switches without wiping the visible chart
    const [isTabLoading, setIsTabLoading] = React.useState(false);

    const isAddition = (values) => {
        return selected.length < values.length
    }

    // Helper: fetch data for an array of symbols for the current tab and
    // replace seriesData entirely. Uses Promise.all to load in parallel,
    // avoiding the stale-closure issue of appending inside a loop.
    const fetchAllCompanies = React.useCallback(async (symbols) => {
        const field = tabs[activeTab].field;
        const results = await Promise.all(
            symbols.map(async (symbol) => {
                const { url } = setUrl(field, symbol);
                const { data: res, error } = await apiFetch({ url });
                if (error || !res) return null;
                return {
                    name: symbol,
                    data: res.map(item => [
                        moment(item[xkey]).toDate().getTime(),
                        Number(item[field])
                    ])
                };
            })
        );
        const valid = results.filter(Boolean);
        if (valid.length) {
            setSeriesData(valid);
            setFetchError(null);
        } else {
            setFetchError({ message: 'Could not load chart data.' });
        }
        setIsTabLoading(false);
        // eslint-disable-next-line
    }, [activeTab]);

    // Effect A — fires on company navigation only.
    // Resets selected to the primary company and fetches its data.
    React.useEffect(() => {
        const primary = { label: querryParams.company__name, value: querryParams.company__symbol };
        setSelected([primary]);
        setSeriesData([]);
        setFetchError(null);
        fetchAllCompanies([querryParams.company__symbol]);
        // eslint-disable-next-line
    }, [history.location.search]);

    // Effect B — fires on tab switch only.
    // Preserves the existing selected companies and re-fetches all of them
    // for the newly active tab's field. Does NOT clear seriesData first so
    // the chart stays visible during the load (no flicker).
    React.useEffect(() => {
        if (!selected.length) return;
        setIsTabLoading(true);
        setFetchError(null);
        fetchAllCompanies(selected.map(s => s.value));
        // eslint-disable-next-line
    }, [activeTab]);

    const handleInputChange = (values) => {
        if (isAddition(values)) {
            // Fetch the newly added company and append using a functional
            // updater to avoid stale-closure issues.
            const newSymbol = values[values.length - 1].value;
            const field = tabs[activeTab].field;
            (async () => {
                const { url } = setUrl(field, newSymbol);
                const { data: res } = await apiFetch({ url });
                if (res) {
                    const newSeries = {
                        name: newSymbol,
                        data: res.map(item => [
                            moment(item[xkey]).toDate().getTime(),
                            Number(item[field])
                        ])
                    };
                    setSeriesData(prev => [...prev, newSeries]);
                }
            })();
        } else {
            // Company removed — filter series to only remaining selected symbols.
            const remaining = new Set(values.map(v => v.value));
            setSeriesData(prev => prev.filter(s => remaining.has(s.name)));
        }
        setSelected(values);
    }

    return (
        <div style={{
            background: T.card,
            borderRadius: 16,
            border: `1px solid ${T.line}`,
            padding: '18px 20px',
            marginBottom: 16,
            boxSizing: 'border-box',
        }}>
            {seriesData.length !== 0 ? (
            <CustomTab
                activeTab={activeTab}
                tabs={tabs}
                seriesData={seriesData}
                setActiveTab={setActiveTab}
                selected={selected}
                setSelected={setSelected}
                handleInputChange={handleInputChange}
                isTabLoading={isTabLoading}
            />
        ) : fetchError ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: 16,
                    fontWeight: 700,
                    color: T.ink,
                }}>
                    {tabs[activeTab].title}
                </div>
                {/* No retry button per product decision */}
                <SectionError message={fetchError.message} />
            </div>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: 16,
                    fontWeight: 700,
                    color: T.ink,
                }}>
                    {tabs[activeTab].title}
                </div>
                <Loader />
            </div>
        )}
        </div>
    )
};

export default GraphContainer;
